import logger from '../utils/logger';
import DOMPurify from 'dompurify';

/**
 * Configuración de DOMPurify para máxima seguridad
 */
const sanitizeConfig = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'span', 'div'],
  ALLOWED_ATTR: ['class', 'id'],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  SANITIZE_DOM: true,
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM_IMPORT: false
};

/**
 * Sanitiza HTML para prevenir ataques XSS
 * @param {string} html - HTML a sanitizar
 * @returns {string} - HTML sanitizado
 */
export const sanitizeHTML = (html, options = {}) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const {
    additionalTags = [],
    additionalAttrs = [],
    allowStyle = false
  } = options;

  try {
    const extraAttrs = allowStyle
      ? [...additionalAttrs, 'style']
      : [...additionalAttrs];

    const config = {
      ...sanitizeConfig,
      ALLOWED_TAGS: Array.from(new Set([
        ...sanitizeConfig.ALLOWED_TAGS,
        ...additionalTags
      ])),
      ALLOWED_ATTR: Array.from(new Set([
        ...sanitizeConfig.ALLOWED_ATTR,
        ...extraAttrs
      ]))
    };

    return DOMPurify.sanitize(html, config);
  } catch (error) {
    logger.error('Error sanitizing HTML:', error);
    return '';
  }
};

/**
 * Sanitiza texto plano (elimina HTML completamente)
 * @param {string} text - Texto a sanitizar
 * @returns {string} - Texto sanitizado
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  try {
    // Eliminar todos los tags HTML
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
  } catch (error) {
    logger.error('Error sanitizing text:', error);
    return '';
  }
};

/**
 * Sanitiza atributos de elementos HTML
 * @param {string} attr - Atributo a sanitizar
 * @returns {string} - Atributo sanitizado
 */
export const sanitizeAttribute = (attr) => {
  if (!attr || typeof attr !== 'string') {
    return '';
  }
  
  try {
    // Solo permitir caracteres alfanuméricos, guiones y guiones bajos
    return attr.replace(/[^a-zA-Z0-9\-_]/g, '');
  } catch (error) {
    logger.error('Error sanitizing attribute:', error);
    return '';
  }
};

/**
 * Sanitiza URLs para prevenir ataques
 * @param {string} url - URL a sanitizar
 * @returns {string} - URL sanitizada
 */
export const sanitizeURL = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }
  
  try {
    // Solo permitir URLs http/https válidas
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    if (urlPattern.test(url)) {
      return url;
    }
    return '';
  } catch (error) {
    logger.error('Error sanitizing URL:', error);
    return '';
  }
};

/**
 * Sanitiza datos de entrada del usuario
 * @param {any} data - Datos a sanitizar
 * @returns {any} - Datos sanitizados
 */
export const sanitizeUserInput = (data) => {
  if (typeof data === 'string') {
    return sanitizeText(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeUserInput(item));
  }
  
  if (data && typeof data === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[sanitizeAttribute(key)] = sanitizeUserInput(value);
    }
    return sanitized;
  }
  
  return data;
};
